/**
 * PDP voice note playback, seek, and transcript toggle.
 * Loaded as a theme asset so it is not deferred by script parking.
 */
if (!window.__abVoiceNoteBound) {
    window.__abVoiceNoteBound = true;

    /**
     * Format seconds as m:ss.
     * @param {number} seconds
     * @returns {string}
     */
    function abVoiceFormatTime(seconds) {
      if (!Number.isFinite(seconds) || seconds < 0) {
        return '0:00';
      }

      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);

      return mins + ':' + String(secs).padStart(2, '0');
    }

    /**
     * @param {HTMLElement} player
     */
    function abVoiceInitPlayer(player) {
      if (player.hasAttribute('data-voice-ready')) {
        return;
      }

      const audio = player.querySelector('[data-voice-audio]');
      const playButton = player.querySelector('[data-voice-play]');
      const wave = player.querySelector('[data-voice-wave]');
      const progress = player.querySelector('[data-voice-progress]');
      const time = player.querySelector('[data-voice-time]');
      const toggle = player.querySelector('[data-voice-toggle]');
      const transcript = player.querySelector('[data-voice-transcript]');
      const showLabel = player.querySelector('[data-voice-label-show]');
      const hideLabel = player.querySelector('[data-voice-label-hide]');
      const waveImgs = player.querySelectorAll('[data-wave-img]');

      if (!audio || !playButton || !wave || !progress || !time) {
        return;
      }

      player.setAttribute('data-voice-ready', '');

      const syncWaveWidth = function() {
        const waveWidth = wave.clientWidth + 'px';

        waveImgs.forEach(function(waveImg) {
          waveImg.style.width = waveWidth;
        });
      };

      const setProgress = function(percent) {
        const clamped = Math.max(0, Math.min(100, percent));

        progress.style.width = clamped + '%';
        wave.setAttribute('aria-valuenow', String(Math.round(clamped)));
      };

      const renderTime = function() {
        const duration = Number.isFinite(audio.duration) ? audio.duration : 0;

        time.textContent = abVoiceFormatTime(audio.currentTime) + ' / ' + abVoiceFormatTime(duration);
      };

      const setPlaying = function(isPlaying) {
        playButton.classList.toggle('is-playing', isPlaying);
        playButton.setAttribute(
          'aria-label',
          isPlaying ? 'Pause voice note' : 'Play voice note'
        );
      };

      const pauseOthers = function() {
        document.querySelectorAll('.ab-voice-note').forEach(function(other) {
          if (other === player) {
            return;
          }

          const otherAudio = other.querySelector('[data-voice-audio]');
          const otherPlay = other.querySelector('[data-voice-play]');

          if (otherAudio && !otherAudio.paused) {
            otherAudio.pause();
          }

          if (otherPlay) {
            otherPlay.classList.remove('is-playing');
            otherPlay.setAttribute('aria-label', 'Play voice note');
          }
        });
      };

      syncWaveWidth();

      if (typeof ResizeObserver !== 'undefined') {
        const waveObserver = new ResizeObserver(syncWaveWidth);

        waveObserver.observe(wave);
      }

      setProgress(0);
      renderTime();

      playButton.addEventListener('click', function() {
        if (audio.paused) {
          pauseOthers();
          audio.play().then(function() {
            setPlaying(true);
          }).catch(function() {
            setPlaying(false);
          });
          return;
        }

        audio.pause();
        setPlaying(false);
      });

      audio.addEventListener('timeupdate', function() {
        if (!audio.duration) {
          return;
        }

        setProgress((audio.currentTime / audio.duration) * 100);
        renderTime();
      });

      audio.addEventListener('loadedmetadata', renderTime);
      audio.addEventListener('durationchange', renderTime);

      if (audio.readyState >= 1) {
        renderTime();
      } else {
        audio.load();
      }

      audio.addEventListener('ended', function() {
        setPlaying(false);
        setProgress(0);
        audio.currentTime = 0;
        renderTime();
      });

      const seekTo = function(clientX) {
        if (!audio.duration) {
          return;
        }

        const rect = wave.getBoundingClientRect();
        const ratio = (clientX - rect.left) / rect.width;
        const next = Math.max(0, Math.min(1, ratio)) * audio.duration;

        audio.currentTime = next;
        setProgress((next / audio.duration) * 100);
        renderTime();
      };

      wave.addEventListener('click', function(event) {
        seekTo(event.clientX);

        if (audio.paused) {
          pauseOthers();
          audio.play().then(function() {
            setPlaying(true);
          }).catch(function() {
            setPlaying(false);
          });
        }
      });

      wave.addEventListener('keydown', function(event) {
        if (!audio.duration) {
          return;
        }

        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
          return;
        }

        event.preventDefault();
        const step = event.key === 'ArrowRight' ? 5 : -5;

        audio.currentTime = Math.max(
          0,
          Math.min(audio.duration, audio.currentTime + step)
        );
        setProgress((audio.currentTime / audio.duration) * 100);
        renderTime();
      });

      if (toggle && transcript && showLabel && hideLabel) {
        toggle.addEventListener('click', function() {
          const expanded = toggle.getAttribute('aria-expanded') === 'true';

          toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
          transcript.hidden = expanded;
          showLabel.hidden = !expanded;
          hideLabel.hidden = expanded;
        });
      }
    }

    const start = function() {
      document.querySelectorAll('.ab-voice-note').forEach(abVoiceInitPlayer);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }

    if (typeof MutationObserver !== 'undefined') {
      new MutationObserver(start).observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    }
  }
